import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ProviderIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getTripProvider } from '../../api/trip-providers/get-trip-provider'
import { updateTripProvider } from '../../api/trip-providers/update-trip-provider'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import TripProviderContactDataTable from '../../components/pages/trip-providers/trip-provider-contact-data-table'
import TripProviderForm from '../../components/pages/trip-providers/trip-provider-form'
import { Paths } from '../../lib/constants/path'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'
import { mapTripProviderToInput } from './utils'

const UpdateTripProviderPage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id))
    return <ItemNotFound title="Identifiant de prestataire de voyage invalide" />

  const { data: tripProvider, isPending: loadingTripProvider } = useQuery({
    queryKey: ['trip-provider', id],
    queryFn: () => getTripProvider(+id)
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateTripProvider,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['trip-provider', id] })
      toast.success('Prestataire de voyage modifié avec succès')
    }
  })

  if (loadingTripProvider) return <LoadingScreen />
  if (!tripProvider) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier un prestataire de voyage'} icon={ProviderIcon} />
      <div className={'h-4'} />
      <Box>
        <TripProviderForm
          defaultValues={mapTripProviderToInput(tripProvider)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TRIP_PROVIDER_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: tripProvider.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: tripProvider.id, ...values })
              navigate(Paths.TRIP_PROVIDER_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: tripProvider.id, ...values })
              navigate(Paths.TRIP_PROVIDER_LIST_PAGE)
            }
          })}
        />
      </Box>
      <div className={'h-4'} />
      <TripProviderContactDataTable tripProviderId={tripProvider.id} />
    </div>
  )
}

export default UpdateTripProviderPage
